import React from 'react';
import { Box, LinearProgress, Skeleton, Stack, Typography, SxProps, Theme } from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import { tokens, DISPLAY_FONT } from '../../theme';

interface StatTileProps {
  label: React.ReactNode;
  value: React.ReactNode;
  /** Mala jednostka za liczba, np. „zł”, „h”, „/ 5”. */
  unit?: React.ReactNode;
  /** Kontekst pod liczba, np. „z 356 800 zł w tym roku”. */
  hint?: React.ReactNode;
  icon?: React.ReactNode;
  /** `hero` — ciemny, wyrozniony kafelek; uzywac najwyzej raz na ekranie. */
  tone?: 'default' | 'hero';
  /** Kolor liczby dla stanow (np. zaległości). */
  valueColor?: string;
  /** Pasek postepu 0–100. */
  progress?: number;
  /** Zmiana procentowa wzgledem poprzedniego okresu. */
  change?: number;
  changeLabel?: string;
  loading?: boolean;
  onClick?: () => void;
  sx?: SxProps<Theme>;
}

/** Jeden kafelek z liczba dla calej aplikacji: etykieta, liczba, kontekst. */
export const StatTile: React.FC<StatTileProps> = ({
  label, value, unit, hint, icon, tone = 'default', valueColor, progress, change, changeLabel, loading, onClick, sx,
}) => {
  const hero = tone === 'hero';
  const positive = (change ?? 0) >= 0;
  return (
    <Box
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } } : undefined}
      sx={{
        height: '100%',
        borderRadius: '14px',
        p: { xs: 2, md: 2.5 },
        display: 'flex',
        flexDirection: 'column',
        gap: 0.75,
        position: 'relative',
        overflow: 'hidden',
        bgcolor: hero ? tokens.aubergine : tokens.paper,
        background: hero ? `linear-gradient(160deg, ${tokens.aubergineRaised} 0%, ${tokens.aubergine} 100%)` : undefined,
        border: `1px solid ${hero ? tokens.aubergineRaised : tokens.line}`,
        color: hero ? '#F4F0F9' : tokens.ink,
        cursor: onClick ? 'pointer' : 'default',
        transition: 'border-color .15s ease, box-shadow .15s ease',
        ...(onClick && {
          '&:hover': { borderColor: hero ? tokens.champagne : '#CFC6DD', boxShadow: '0 4px 14px rgba(28,23,38,0.06)' },
          '&:focus-visible': { outline: `2px solid ${tokens.plum}`, outlineOffset: 2 },
        }),
        ...((sx as object) || {}),
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
        <Typography sx={{ fontSize: '0.84375rem', fontWeight: 600, color: hero ? '#BDB3CE' : tokens.muted, lineHeight: 1.4 }}>
          {label}
        </Typography>
        {icon && (
          <Box sx={{ color: hero ? tokens.champagne : tokens.subtle, display: 'flex', '& .MuiSvgIcon-root': { fontSize: 20 } }}>{icon}</Box>
        )}
      </Stack>
      {loading ? (
        <Skeleton variant="rounded" width="60%" height={34} sx={{ bgcolor: hero ? 'rgba(255,255,255,0.08)' : undefined }} />
      ) : (
        <Typography
          component="div"
          sx={{
            fontSize: { xs: '1.625rem', md: '1.875rem' },
            fontWeight: 750,
            letterSpacing: '-0.02em',
            lineHeight: 1.1,
            fontVariantNumeric: 'tabular-nums',
            color: valueColor || 'inherit',
            wordBreak: 'break-word',
          }}
        >
          {value}
          {unit && (
            <Box component="span" sx={{ fontSize: '0.6em', fontWeight: 650, ml: 0.5, letterSpacing: 0, color: hero ? '#D9D0E6' : tokens.inkSoft }}>
              {unit}
            </Box>
          )}
        </Typography>
      )}
      {typeof progress === 'number' && (
        <LinearProgress
          variant="determinate"
          value={Math.max(0, Math.min(100, progress))}
          sx={{
            mt: 0.5,
            bgcolor: hero ? 'rgba(255,255,255,0.12)' : tokens.surfaceStrong,
            '& .MuiLinearProgress-bar': { background: hero ? `linear-gradient(90deg, #B38B4F, #E2C38E)` : tokens.plum },
          }}
        />
      )}
      {(hint || typeof change === 'number') && (
        <Stack direction="row" alignItems="center" gap={0.75} flexWrap="wrap" sx={{ mt: 'auto' }}>
          {typeof change === 'number' && (
            <Stack direction="row" alignItems="center" gap={0.25} sx={{ color: positive ? tokens.success : tokens.danger }}>
              {positive ? <TrendingUpIcon sx={{ fontSize: 16 }} /> : <TrendingDownIcon sx={{ fontSize: 16 }} />}
              <Typography sx={{ fontSize: '0.8125rem', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                {positive ? '+' : ''}{change}%
              </Typography>
            </Stack>
          )}
          {(hint || changeLabel) && (
            <Typography component="div" sx={{ fontSize: '0.84375rem', color: hero ? '#CFC6DC' : tokens.inkSoft, lineHeight: 1.45 }}>
              {typeof change === 'number' && changeLabel ? changeLabel : hint}
            </Typography>
          )}
        </Stack>
      )}
    </Box>
  );
};

/** Duza liczba w stylu Panelu, gdy potrzebny jest sam naglowek bez kafelka. */
export const displayNumberSx = { fontFamily: DISPLAY_FONT } as const;

export default StatTile;
